import { Component, Inject, OnInit } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  CalibrationInformation,
  LoadcellInformationResponse,
} from "app/api/models";
import { EmailService } from "app/api/services";
import { AppLoaderService } from "app/core/services/app-loader/app-loader.service";
import { genericSendEmailError } from "../../../../shared/helpers/email-sending-helper";
import { multipleError } from "../../../../shared/helpers/error-helper";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Form10AStatus } from "app/api/models/form-10-a-status";
import { ExaminationReason } from "app/api/models/examination-reason";
import { CommonModule, DatePipe } from "@angular/common";
import { FlexLayoutModule } from "@angular/flex-layout";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatListModule } from "@angular/material/list";
import { MatMenuModule } from "@angular/material/menu";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatTooltipModule } from "@angular/material/tooltip";
import { DLMSharedModule } from "app/views/calibrations/shared/shared.module";
import { DragScrollModule } from "ngx-drag-scroll";
import { NgxPrintModule } from "ngx-print";
import { PdfComponent } from "app/core/components/pdf/pdf.component";
import autoTable, { Styles, UserOptions } from "jspdf-autotable";

@Component({
  selector: "app-rot-certificate",
  templateUrl: "./rot-certificate.component.html",
  styleUrls: ["./rot-certificate.component.scss", "./../../../../_styles/calibration-overrides.scss"],
  standalone: true,
  imports: [
    CommonModule,
    DLMSharedModule,
    MatListModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatInputModule,
    MatMenuModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    FlexLayoutModule,
    DragScrollModule,
    NgxPrintModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatRadioModule,
  ]
})
export class RotCertificateComponent extends PdfComponent implements OnInit {
  model: LoadcellInformationResponse;
  calibration: CalibrationInformation;
  
  public GetRotTitle = () => `Examination-Report-${this.model?.loadcell?.loadcellId}.pdf`;

  constructor(
    public dialogRef: MatDialogRef<RotCertificateComponent>,
    private emailService: EmailService,
    private loader: AppLoaderService,
    private snack: MatSnackBar,
    private datePipe: DatePipe,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.data) {
      this.model = this.data.loadcellInfo;
      this.calibration = this.data.calibration;
    }
  }

  getTodaysDate() {
    return new Date().toUTCString();
  }

  getNextExaminationDate() {
    return new Date(
      new Date().setMonth(new Date(this.calibration?.date).getMonth() + 6)
    );
  }

  private preparePdf(callback: (pdfDoc: jsPDF) => any) {
    let PDF = new jsPDF("l", "mm", "a4", true);

    this.loader.open();

    //title
    this.addTextToPdf(PDF, [[`Report Of Thorough Examination`]], 16, 0, 35, null, true);
    this.addTextToPdf(PDF, [['This report meets the requirements of schedule 1 of regulation 10 of LOLER 1998']], 12, 0, ((PDF as any).lastAutoTable.finalY || 15) - 4, null, true);

    var cellStyle: Partial<Styles> = { lineColor: "#000000", textColor: "#000000", fontSize: 8, lineWidth: 0.2, cellWidth: 28, cellPadding: {vertical: 2, horizontal: 2}, fontStyle: 'normal', };
    var whiteCell: Partial<Styles> = { ...cellStyle, fillColor: "#FFFFFF" };
    var borderlessCell: Partial<Styles> = { ...whiteCell, lineColor: '#FFFFFF', cellWidth: 70, fontSize: 9, cellPadding: {vertical: 2, horizontal: 3} };

    var autoTableOptions: UserOptions = {theme: "grid", body: [], tableWidth: 280, margin: {horizontal: 8, top: 16, bottom: 16} };

    //header text
    autoTable(PDF, {
      ...autoTableOptions,
      startY: ((PDF as any).lastAutoTable.finalY || 15) + 4,
      head: [
          [
            { content: "Company Name & Address: Dynamic Load Monitoring (UK) Ltd, DLM House, Nursling, Southampton", styles: borderlessCell, colSpan: 3 },
            { content: "Phone: 02380 741700", styles: { ...borderlessCell, halign:'right' } },
          ],
          [
            { content: `Report No: ${this.model?.form10a?.form10aId ?? ''}`, styles: borderlessCell },
            { content: `Customer No: ${this.model?.form10a?.form10aId ?? ''}`, styles: borderlessCell },
            { content: `Date of Report: ${this.datePipe.transform(this.getTodaysDate(), 'd/MM/YYYY')}`, styles: borderlessCell },
            { content: `Color Code (if required): N/A`, styles: { ...borderlessCell, halign:'right' } },
          ],
      ],
    });

    //content table
    autoTable(PDF, {
      ...autoTableOptions,
      startY: ((PDF as any).lastAutoTable.finalY || 15) + 4,
      head: [
          [
            { content: `Name & Address of the employer for whom the examination was made:\n${this.model?.loadcell?.customerName}`, styles: whiteCell, colSpan: 4 },
            { content: `Address of the premises at which the examination was made:\nSO18 1DH\nSouthampton`, styles: whiteCell, colSpan: 4 },
            { content: `Status:\nND - No Defect\nSDR - See Defect Report\nNF - Not Found`, styles: whiteCell, colSpan: 2 },
          ],
          [
            { content: `Identification Number`,                         styles: {...whiteCell, halign: 'center', fontStyle: 'bold'} },
            { content: `Description`,                                   styles: {...whiteCell, halign: 'center', fontStyle: 'bold'} },
            { content: `WLL\nor\nSWL`,                                  styles: {...whiteCell, halign: 'center', fontStyle: 'bold'} },
            { content: `Date of Last Thorough Examination`,             styles: {...whiteCell, halign: 'center', fontStyle: 'bold'} },
            { content: `Date of this Thorough Examination`,             styles: {...whiteCell, halign: 'center', fontStyle: 'bold'} },
            { content: `Latest date of the next thorough examination`,  styles: {...whiteCell, halign: 'center', fontStyle: 'bold'} },
            { content: `Reason for Examination\n(See Below)`,           styles: {...whiteCell, halign: 'center', fontStyle: 'bold'} },
            { content: `Details of any test`,                           styles: {...whiteCell, halign: 'center', fontStyle: 'bold'} },
            { content: `Status\n(See above)`,                           styles: {...whiteCell, halign: 'center', fontStyle: 'bold'} },
            { content: `Safe to Use\nYes or No`,                        styles: {...whiteCell, halign: 'center', fontStyle: 'bold'} },
          ],
          ...this.model?.form10a?.roTEItems?.map(item => [
            { content: `RTE-${item.roTEItemId ?? ''}`,                                        styles: {...whiteCell, cellPadding: {vertical: 1, horizontal: 2} } },
            { content: item.roTEDescription ?? '',                                            styles: {...whiteCell, cellPadding: {vertical: 1, horizontal: 2} } },
            { content: this.calibration?.swl ?? '',                                           styles: {...whiteCell, cellPadding: {vertical: 1, horizontal: 2} } },
            { content: `N/A`,                                                                 styles: {...whiteCell, cellPadding: {vertical: 1, horizontal: 2} } },
            { content: this.datePipe.transform(this.calibration?.date, "dd/MM/yyyy"),         styles: {...whiteCell, cellPadding: {vertical: 1, horizontal: 2} } },
            { content: this.datePipe.transform(this.getNextExaminationDate(), "dd/MM/yyyy"),  styles: {...whiteCell, cellPadding: {vertical: 1, horizontal: 2} } },
            { content: this.getReasonCode(item.reasonForExamination) ?? '',                   styles: {...whiteCell, cellPadding: {vertical: 1, horizontal: 2} } },
            { content: item.detailsOfAnyTest ?? '',                                           styles: {...whiteCell, cellPadding: {vertical: 1, horizontal: 2} } },
            { content: this.getStatusCode(item.status) ?? '',                                 styles: {...whiteCell, cellPadding: {vertical: 1, horizontal: 2} } },
            { content: item.safeToUse ? "yes" : "no",                                         styles: {...whiteCell, cellPadding: {vertical: 1, horizontal: 2} } },
          ]),
      ],
    });

    //reasons and signatures
    autoTable(PDF, {
      ...autoTableOptions,
      startY: ((PDF as any).lastAutoTable.finalY || 15),
      head: [
          [
            { content: `Reason for Examination`,        styles: {...whiteCell, valign: 'middle', fontStyle: 'bold', cellWidth: 46} },
            { content: `Installation: A`,               styles: {...whiteCell, valign: 'middle', cellWidth: 47} },
            { content: `6 Monthly: B`,                  styles: {...whiteCell, valign: 'middle', cellWidth: 47} },
            { content: `12 Monthly: C`,                 styles: {...whiteCell, valign: 'middle', cellWidth: 46} },
            { content: `Written Scheme: D`,             styles: {...whiteCell, valign: 'middle', cellWidth: 47} },
            { content: `Exceptional Circumstances: E`,  styles: {...whiteCell, valign: 'middle', cellWidth: 47} },
          ],
          [
            { content: `Name & qualifications of the person making this report:\n${this.model?.form10a?.nameAndQualificationsOfThePerson ?? ''}`, styles: whiteCell, colSpan: 3 },
            { content: `Name of the person authenticating this report:\n${this.model?.form10a?.nameOfThePersonAuthenticating ?? ''}` +
                        `\n\n\n\n\nSignature:`, styles: {...whiteCell}, colSpan: 3 },
          ],
      ],
    });


    this.addSignature(PDF, ((PDF as any).lastAutoTable.finalY || 15) - 16).then(PDF => {
      this.addHeader(PDF).then(PDF => {
        this.loader.close();
        callback(PDF);
      });
    });
  }
  
  async addHeader(PDF: jsPDF) {
    var canvas = await this.createCanvasFromElement("print-header");
    this.addCanvasToPdfXY(canvas, PDF, 0, 0);
    return PDF;
  }

  async addSignature(PDF: jsPDF, yOffset: number) {
    var canvas = await this.createCanvasFromElement("print-signature");
    this.addCanvasToPdfXY(canvas, PDF, yOffset, 165, 104);
    return PDF;
  }
  
  //override
  addCanvasToPdfXY(canvas: HTMLCanvasElement, PDF: jsPDF, y: number, x: number, width: number = null) {
    let fileWidth = width ?? 297;
    let fileHeight = (canvas.height * fileWidth) / canvas.width;

    const FILEURI = canvas.toDataURL("image/png");
    PDF.addImage(FILEURI, "PNG", x, y, fileWidth, fileHeight);
  }

  public openPDF(): void {
    this.preparePdf((PDF) =>
      PDF.save(this.GetRotTitle())
    );
  }

  emailReportOfExamination() {
    this.preparePdf((PDF) => {
      var blobPDF = new Blob([PDF.output("blob")], { type: "application/pdf" });

      this.emailService
        .apiEmailSendRotEmailPost$Json({
          calibrationId: this.calibration.calibrationId,
          loadcellId: this.model.loadcell.loadcellId,
          body: { formFile: blobPDF },
        })
        .subscribe(
          (data) => {
            this.loader.close();

            if (data?.data) {
              return this.snack.open("Email sent to customer!", "OK", {
                duration: 4000,
              });
            } else if (data?.errors?.length > 0) {
              return multipleError(data.errors);
            }

            return genericSendEmailError(this.snack);
          },
          (err) => {
            genericSendEmailError(this.snack);
            this.loader.close();
          }
        );
    });
  }

  printReportOfExamination() {
    this.preparePdf((PDF) => {
      PDF.autoPrint();
      window.open(
        PDF.output("bloburl"),
        "_blank",
        "toolbar=0,location=0,menubar=0"
      );
    });
  }

  getStatusText(status: Form10AStatus) {
    switch (status) {
      case Form10AStatus.NoDefect:
        return "No Defect";
      case Form10AStatus.SeeDefectReport:
        return "See Defect Report";
      case Form10AStatus.NotFound:
        return "Not Found";
      default:
        return "N/A";
    }
  }

  getReasonText(reason: ExaminationReason) {
    switch (reason) {
      case ExaminationReason.Installation:
        return "Installation";
      case ExaminationReason.SixMonthly:
        return "Six Monthly";
      case ExaminationReason.TwelveMonthly:
        return "Twelve Monthly";
      case ExaminationReason.WrittenScheme:
        return "Written Scheme";
      case ExaminationReason.ExceptionalCircumstances:
        return "Exceptional Circumstances";
      default:
        return "N/A";
    }
  }

  getReasonCode(reason: ExaminationReason) {
    switch (reason) {
      case ExaminationReason.Installation:
        return "A";
      case ExaminationReason.SixMonthly:
        return "B";
      case ExaminationReason.TwelveMonthly:
        return "C";
      case ExaminationReason.WrittenScheme:
        return "D";
      case ExaminationReason.ExceptionalCircumstances:
        return "E";
      default:
        return "N/A";
    }
  }

  getStatusCode(status: Form10AStatus) {
    switch (status) {
      case Form10AStatus.NoDefect:
        return "ND";
      case Form10AStatus.SeeDefectReport:
        return "SDR";
      case Form10AStatus.NotFound:
        return "NF";
      default:
        return "N/A";
    }
  }

  // emailReportOfExamination() {
  //   this.loader.open;
  //   this.emailService
  //     .sendReportOfExaminationEmail$Json$Response({ body: this.calibration })
  //     .subscribe(
  //       (data) => {
  //         this.loader.close();

  //         if (data?.body?.data) {
  //           return this.snack.open("Email sent to customer!", "OK", {
  //             duration: 4000,
  //           });
  //         } else if (data?.body?.errors?.length > 0) {
  //           return multipleError(data.body.errors);
  //         }

  //         return genericSendEmailError();
  //       },
  //       (err) => {
  //         genericSendEmailError();
  //         this.loader.close();
  //       }
  //     );
  // }
}
